import React, { useState } from 'react';
import { version } from '../../package.json';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Play, ChevronDown, Trophy, Compass, Sparkles, CheckCircle, GraduationCap, Printer } from 'lucide-react';
import { Course, getCourseIcon, ModuleId } from '../../types';
import { useAcademyStore } from '../../store/academyStore';
import { COURSES, MODULE_SEQUENCE } from '../../data/curriculum';
import { QuizRunner } from '../features/Quiz/QuizRunner';
import { useSfx } from '../../store/sfxStore';

// Helper to check if a specific module is locked for the current user
// Helper to check if a specific module is locked for the current user
const isModuleLocked = (moduleId: string, courseId: string, completedModules: string[] = [], unlockedCourses: string[] = []) => {
    // 1. If the course is explicitly unlocked, and this is the first module, unlock it.
    const course = COURSES.find(c => c.id === courseId);
    if (course && course.modules[0].id === moduleId) {
        if (unlockedCourses.includes(courseId)) {
            return false;
        }
        // Fallback: If it's the first course (Course 0), it's always unlocked (unless we lock it via data)
        if (moduleId === MODULE_SEQUENCE[0]) return false;
    }

    // 2. Strict Sequential Check
    const validModuleId = moduleId as ModuleId;
    const index = MODULE_SEQUENCE.indexOf(validModuleId);

    if (index === 0) return false; // Very first module of the app
    if (index === -1) return true; // Unknown module

    const previousModuleId = MODULE_SEQUENCE[index - 1];
    return !completedModules.includes(previousModuleId);
};

const CourseSection = ({ course }: { course: Course }) => {
    const [isOpen, setIsOpen] = useState(true);
    const Icon = getCourseIcon(course.id);
    const { currentUser, progress } = useAcademyStore();
    const { play } = useSfx();
    const userProgress = currentUser ? progress[currentUser.id] : null;

    const completedModules = course.modules.filter(m =>
        userProgress?.completedModules?.includes(m.id)
    ).length;
    const progressPercent = (completedModules / course.modules.length) * 100;
    const isCourseCompleted = completedModules === course.modules.length;
    const hasCertificate = userProgress?.completedCourses?.includes(course.id);
    const [showQuiz, setShowQuiz] = useState(false);

    // Handle module click - quiz modules open the QuizRunner
    const handleModuleClick = (module: typeof course.modules[0], isLocked: boolean, e: React.MouseEvent) => {
        if (isLocked) {
            e.preventDefault();
            return;
        }
        if (module.type === 'quiz') {
            e.preventDefault();
            setShowQuiz(true);
        }
        // For regular modules, let the Link handle navigation
    };

    // Get destination URL for a module
    const getModuleDestination = (module: typeof course.modules[0]) => {
        if (module.type === 'quiz') return '#';
        if (module.id === 'spreadsheet-service') return '/sheet-wizard';
        if (module.id === 'auth') return '/auth';
        return `/${module.id}`;
    };

    return (
        <>
            {/* Quiz Modal */}
            <AnimatePresence>
                {showQuiz && (
                    <motion.div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowQuiz(false)}
                    >
                        <motion.div
                            className="w-full max-w-2xl"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <QuizRunner
                                courseId={course.id}
                                courseTitle={course.title}
                                onClose={() => setShowQuiz(false)}
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                className="overflow-hidden bg-white rounded-3xl shadow-lg border-2 border-slate-100 hover:shadow-2xl transition-all duration-500"
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
                {/* Course Header */}
                <div
                    onClick={() => { setIsOpen(!isOpen); play('click'); }}
                    onMouseEnter={() => play('hover')}
                    className="relative p-8 cursor-pointer flex items-center justify-between group"
                >
                    {/* Progress Bar Background */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100">
                        <motion.div
                            className={`h-full bg-gradient-to-r ${course.gradient}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPercent}%` }}
                            transition={{ duration: 1, delay: 0.2 }}
                        />
                    </div>

                    <div className="flex items-center gap-6">
                        {/* Course Icon */}
                        <motion.div
                            className={`w-20 h-20 rounded-2xl flex items-center justify-center text-white shadow-xl bg-gradient-to-br ${course.gradient}`}
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        >
                            <Icon size={36} strokeWidth={2.5} />
                        </motion.div>

                        <div>
                            <h2 className="text-2xl font-black text-slate-800 font-comic tracking-wide group-hover:text-green-600 transition-colors flex items-center gap-3">
                                {course.title}
                                {progressPercent === 100 && (
                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                                        <Trophy size={14} /> Complete!
                                    </span>
                                )}
                            </h2>
                            <p className="text-lg text-slate-500 font-medium mt-1">{course.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="text-slate-400 font-bold">{course.modules.length} Modules</span>
                                <span className="text-green-600 font-bold">{completedModules} Completed</span>
                            </div>
                        </div>
                    </div>

                    <motion.div
                        className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-100 group-hover:bg-green-100 transition-colors"
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <ChevronDown className="text-slate-600 group-hover:text-green-600" size={24} />
                    </motion.div>
                </div>

                {/* Modules Grid */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="bg-gradient-to-b from-slate-50 to-white"
                        >
                            <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {course.modules.map((module, index) => {
                                    // Determine lock status dynamically
                                    const isLocked = isModuleLocked(
                                        module.id,
                                        course.id,
                                        userProgress?.completedModules,
                                        userProgress?.unlockedCourses
                                    );
                                    const isCompleted = userProgress?.completedModules?.includes(module.id);
                                    const isQuiz = module.type === 'quiz';

                                    return (
                                        <motion.div
                                            key={module.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                        >
                                            <Link
                                                to={isLocked ? '#' : getModuleDestination(module)}
                                                onClick={(e) => {
                                                    handleModuleClick(module, isLocked, e);
                                                    if (isLocked) play('error');
                                                    else play('click');
                                                }}
                                                onMouseEnter={() => {
                                                    if (!isLocked) play('hover');
                                                }}
                                                className={`module-card block ${isLocked ? 'module-card-locked' : ''} ${isCompleted ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-slate-50' : ''} ${isQuiz && !isLocked ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-50' : ''}`}
                                            >
                                                {/* Card Glow Effect */}
                                                {!isLocked && (
                                                    <div className={`absolute inset-0 rounded-2xl ${isQuiz ? 'bg-gradient-to-br from-amber-100/0 to-amber-100/50' : 'bg-gradient-to-br from-green-100/0 to-green-100/50'} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                                                )}

                                                <div className="relative z-10">
                                                    <div className="flex justify-between items-start mb-4">
                                                        <div className={`module-icon ${isLocked
                                                            ? 'bg-slate-200 text-slate-400'
                                                            : isCompleted
                                                                ? 'bg-green-500 text-white shadow-lg shadow-green-200'
                                                                : isQuiz
                                                                    ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-200'
                                                                    : `bg-gradient-to-br ${course.gradient} text-white shadow-lg`
                                                            }`}>
                                                            {isLocked ? <Lock size={20} /> : isCompleted ? <CheckCircle size={20} /> : isQuiz ? <GraduationCap size={20} /> : <Play size={20} fill="currentColor" />}
                                                        </div>
                                                        {isCompleted && (
                                                            <motion.div
                                                                initial={{ scale: 0 }}
                                                                animate={{ scale: 1 }}
                                                                className="text-green-500 bg-green-100 rounded-full p-1"
                                                            >
                                                                <CheckCircle size={16} strokeWidth={3} />
                                                            </motion.div>
                                                        )}
                                                    </div>

                                                    <h3 className={`text-lg font-bold mb-2 ${isLocked ? 'text-slate-500' : isQuiz ? 'text-amber-800 group-hover:text-amber-700' : 'text-slate-800 group-hover:text-green-700'
                                                        }`}>
                                                        {module.title}
                                                    </h3>
                                                    <p className="text-base text-slate-500 font-medium">{module.description}</p>

                                                    {!isLocked && (
                                                        <div className={`mt-4 flex items-center gap-2 ${isQuiz ? 'text-amber-600' : 'text-green-600'} font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity`}>
                                                            {isQuiz ? <GraduationCap size={14} /> : <Play size={14} fill="currentColor" />}
                                                            {isQuiz ? 'Take Exam' : 'Start Module'}
                                                        </div>
                                                    )}
                                                </div>
                                            </Link>
                                        </motion.div>
                                    )
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </>
    );
};

export default function Dashboard() {
    // Note: TopBar now handles the header including level/xp/user profile.
    const { currentUser, progress, completeCourse } = useAcademyStore();
    const navigate = useNavigate();

    // Self-Healing: Ensure next courses are unlocked if exams are passed
    React.useEffect(() => {
        if (!currentUser || !progress[currentUser.id]) return;
        const userProgress = progress[currentUser.id];

        COURSES.forEach((course, index) => {
            const finalExamId = `${course.id}-final-exam`;
            const isExamPassed = userProgress.completedModules.includes(finalExamId);

            if (isExamPassed && index < COURSES.length - 1) {
                const nextCourse = COURSES[index + 1];
                const isNextUnlocked = userProgress.unlockedCourses.includes(nextCourse.id);

                if (!isNextUnlocked) {
                    console.warn(`[Dashboard Audit] Unlocking ${nextCourse.id} because ${finalExamId} is complete.`);
                    completeCourse(course.id);
                }
            }
        });
    }, [currentUser, progress, completeCourse]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-slate-50 font-sans text-slate-900 selection:bg-green-200 selection:text-green-900 pb-20 overflow-x-hidden relative pt-24">

            {/* Animated Background Blobs */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 left-0 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-float" />
                <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
                <div className="absolute bottom-0 left-1/3 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
            </div>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 relative z-10">

                {/* Welcome Banner */}
                <motion.div
                    className="card-glass p-10 mb-12 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-green-200/50 to-transparent rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-200/30 to-transparent rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10 flex-col md:max-w-xl">
                        <motion.h2
                            className="text-4xl lg:text-5xl font-black text-slate-800 font-comic mb-4"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            Ready for Adventure?
                            <motion.span
                                className="inline-block ml-3"
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                            >
                                🏕️
                            </motion.span>
                        </motion.h2>
                        <p className="text-xl text-slate-600 font-medium">
                            Let's earn some badges!
                        </p>
                    </div>

                    <div className="relative z-10 w-full md:w-auto mt-4 md:mt-0">
                        {(() => {
                            const p = currentUser ? progress[currentUser.id] || { completedModules: [], unlockedCourses: [] } : { completedModules: [], unlockedCourses: [] };
                            
                            let nextCourse = null;
                            let nextModule = null;

                            // Find the first module that is not completed but is unlocked
                            for (const course of COURSES) {
                                for (const module of course.modules) {
                                    if (!p.completedModules.includes(module.id)) {
                                        const locked = isModuleLocked(module.id, course.id, p.completedModules, p.unlockedCourses);
                                        if (!locked) {
                                            nextCourse = course;
                                            nextModule = module;
                                            break;
                                        }
                                    }
                                }
                                if (nextCourse) break;
                            }

                            if (!nextCourse) {
                                return (
                                    <motion.div
                                        whileHover={{ scale: 1.05, rotate: 2 }}
                                        transition={{ type: "spring", stiffness: 300 }}
                                        onClick={() => navigate('/diploma')}
                                        className="cursor-pointer bg-gradient-to-br from-amber-100 to-yellow-50 text-amber-900 font-bold px-8 py-5 rounded-3xl flex items-center gap-6 border-4 border-amber-300 shadow-xl"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center drop-shadow-md border-4 border-white animate-bounce-subtle">
                                            <Trophy className="text-amber-500 w-10 h-10" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-black text-amber-600 uppercase tracking-widest flex items-center gap-1">
                                                <Sparkles size={14} /> Master Scout Unlocked
                                            </div>
                                            <span className="text-2xl font-black font-comic">Claim Diploma</span>
                                        </div>
                                    </motion.div>
                                );
                            }

                            const dest = nextModule?.type === 'quiz' ? '#' : (nextModule?.id === 'spreadsheet-service' ? '/sheet-wizard' : (nextModule?.id === 'auth' ? '/auth' : `/${nextModule?.id}`));
                            const IconC = getCourseIcon(nextCourse.id as any);

                            return (
                                <motion.div
                                    whileHover={{ scale: 1.05, rotate: 2 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                    onClick={() => navigate(dest)}
                                    className="cursor-pointer bg-white text-slate-800 font-bold px-8 py-5 rounded-3xl flex flex-col sm:flex-row items-center gap-6 border-4 border-slate-200 shadow-xl group hover:border-green-400 transition-colors"
                                >
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${nextCourse.gradient} flex flex-shrink-0 items-center justify-center shadow-lg transform group-hover:-rotate-3 transition-transform animate-bounce-subtle border-4 border-white ring-2 ring-slate-100`}>
                                        <IconC className="text-white w-8 h-8 drop-shadow" />
                                    </div>
                                    <div className="text-center sm:text-left">
                                        <div className="text-xs font-black text-green-600 uppercase tracking-widest flex items-center justify-center sm:justify-start gap-1">
                                            <Compass size={14} /> Current Objective
                                        </div>
                                        <div className="text-2xl font-black font-comic truncate max-w-[200px] leading-tight mt-1">{nextCourse.title}</div>
                                        <div className="text-sm text-slate-500 font-medium truncate max-w-[200px] mt-0.5">Start: {nextModule?.title}</div>
                                    </div>
                                </motion.div>
                            );
                        })()}
                    </div>
                </motion.div>

                {/* Section Header */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl flex items-center justify-center text-white shadow-xl">
                            <Compass size={28} />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-slate-800">Mission Log</h2>
                            <p className="text-lg text-slate-500">Choose your next adventure</p>
                        </div>
                    </div>
                    <div className="badge badge-success text-base px-5 py-2.5">
                        <Sparkles size={18} />
                        {COURSES.length} Active Courses
                    </div>
                </div>

                {/* Courses Grid */}
                <motion.div
                    initial="hidden"
                    animate="show"
                    variants={{
                        hidden: { opacity: 0 },
                        show: { opacity: 1, transition: { staggerChildren: 0.1 } }
                    }}
                    className="space-y-8"
                >
                    {COURSES.map(course => (
                        <motion.div
                            key={course.id}
                            variants={{
                                hidden: { y: 20, opacity: 0 },
                                show: { y: 0, opacity: 1 }
                            }}
                        >
                            <CourseSection course={course} />
                        </motion.div>
                    ))}
                </motion.div>

                {/* Footer */}
                <div className="mt-24 pt-12 border-t-4 border-slate-200 border-dashed text-center pb-20">
                    <motion.div
                        className="inline-flex items-center gap-3 opacity-60 hover:opacity-100 transition-all cursor-pointer group"
                        whileHover={{ scale: 1.05 }}
                    >
                        <img src="/assets/branding/logo.png" className="h-12 w-auto grayscale group-hover:grayscale-0 transition-all" />
                        <span className="font-comic font-black text-2xl text-slate-400 group-hover:text-green-600 transition-colors">Grounded Growth Academy</span>
                    </motion.div>
                    <p className="mt-3 text-base text-slate-400 font-mono font-bold tracking-wide">EST. 2026 • "CODE STRONGER TOGETHER"</p>
                    <p className="mt-2 text-xs text-slate-300 font-mono">v{version}</p>
                </div>
            </main>
        </div>
    );
}
